import { CustomerServiceOutlined } from "@ant-design/icons";
import flagsupConstants from "constants/flagsup";
import { actionConstants, appConstants, resourceConstants } from "constants/permission";
import { lazy } from "react";
import { userUtils } from "utils";
import { t } from "utils/i18n";

const { permission } = userUtils;
const { STAFF_BFF } = appConstants;
const { SUPPORT_ASSISTANT } = resourceConstants;
const { READ, READ_INTERNAL } = actionConstants;
const { FLAG_KEYS } = flagsupConstants;

const SupportRequestList = lazy(() => import("pages/SupportRequestList"));
const SupportChannel = lazy(() => import("pages/SupportChannel"));
const InternalSupport = lazy(() => import("pages/InternalSupport"));
const SupportDashboard = lazy(() => import("pages/SupportDashboard"));
const MySupportRequests = lazy(() => import("pages/MySupportRequests"));

// OMNI-TIC2026 — the route carries the same flag + permission gate as the drawer launcher.
// Hiding the menu is UX only; the BFF re-checks permission, ownership and tenant on every read.
//
// The gate now uses the epic's OWN capabilities (T12/T49), matching the BFF slug for slug:
//   /support/requests  -> staff-bff:support_assistant:read:internal   (internal audience)
//   /support/channel   -> staff-bff:support_assistant:read            (staff-seller audience)
// The two are separate permissions, not one with a qualifier: if they ever collapse, a seller's staff
// session gains the internal audience (BR-BP4-03).
//
// Note on the direction of the route filter's prefix match (`utils/user.ts:37` uses startsWith): a
// seller-only session does NOT satisfy `...read:internal`, which is the direction that matters here. The
// reverse direction — internal-only reaching a staff-seller surface — is checked EXACTLY in
// components/SupportLauncherMount.tsx.
//
// [Needs Info: the four permissions still have to be CREATED in IAM and granted to the pilot cohort
// (T52 — IAM owner + PO). Until then every gated route and the launcher stay hidden, which is correct.]
export default [
  {
    path: "/support",
    name: t("SupportChannel.MenuRoot"),
    icon: CustomerServiceOutlined,
    children: ["/support/my-requests", "/support/requests", "/support/dashboard"],
  },
  // OMNI-TIC2026 — "Yêu cầu của tôi". Mục menu đầu tiên của `Hỗ trợ` vì nó là mục có ĐỐI TƯỢNG RỘNG
  // NHẤT: hai mục còn lại đòi `read:internal` (đội Support Teko), mục này chỉ đòi `read`.
  //
  // Quyền là `READ`, KHÔNG phải `READ_INTERNAL`, và đây là chỗ dễ sai nhất trong file: hai mục kia
  // đọc phiếu của NGƯỜI KHÁC, mục này chỉ đọc phiếu của chính người đang đăng nhập. Dùng nhầm
  // `READ_INTERNAL` thì hoặc seller không bao giờ thấy menu, hoặc một phiên nội bộ được coi là chủ
  // phiếu — đúng cái BR-BP4-03 cấm.
  //
  // Vì sao cần mục menu chứ không chỉ nút trong panel: `SupportLauncher` trả `null` ở mọi màn ngoài
  // ba module pilot (`SupportLauncher/index.tsx`), nên nếu chỉ có nút trong panel thì đường tra cứu
  // biến mất khỏi phần lớn app — kể cả với người vừa gửi yêu cầu hôm qua.
  {
    exact: true,
    path: "/support/my-requests",
    name: t("SupportLauncher.MyRequests"),
    component: MySupportRequests,
    permissions: [permission(STAFF_BFF, SUPPORT_ASSISTANT, READ)],
    flagKey: FLAG_KEYS.SUPPORT_ASSISTANT_WORKSPACE,
  },
  {
    exact: true,
    path: "/support/requests",
    name: t("SupportRequestList.Title"),
    component: SupportRequestList,
    permissions: [permission(STAFF_BFF, SUPPORT_ASSISTANT, READ_INTERNAL)],
    flagKey: FLAG_KEYS.SUPPORT_ASSISTANT_WORKSPACE,
  },
  // Dashboard hỗ trợ — mục menu thứ 2 của "Hỗ trợ". Cùng audience `internal` và cùng gate quyền/cờ
  // với `/support/requests`: đây là số liệu tổng hợp của TOÀN BỘ phạm vi seller mà phiên phụ trách,
  // không phải dữ liệu của riêng một seller ⇒ vẫn là `read:internal`, không phải `read`.
  //
  // Nằm cạnh `/support/requests` chỉ để hai mục menu của "Hỗ trợ" đứng liền nhau; `/support/dashboard`
  // không chồng path với `/support/requests/:code` nên thứ tự khai báo không ảnh hưởng gì.
  {
    exact: true,
    path: "/support/dashboard",
    name: t("SupportDashboard.Title"),
    component: SupportDashboard,
    permissions: [permission(STAFF_BFF, SUPPORT_ASSISTANT, READ_INTERNAL)],
    flagKey: FLAG_KEYS.SUPPORT_ASSISTANT_WORKSPACE,
  },
  // T47a — đích của [Mở xử lý] ở danh sách: màn NỘI BỘ của một yêu cầu. Không nằm trong menu (mở từ
  // danh sách), và cùng gate quyền/cờ với `/support/requests` vì cùng audience `internal`.
  {
    exact: true,
    path: "/support/requests/:code",
    name: t("InternalSupport.Title"),
    component: InternalSupport,
    permissions: [permission(STAFF_BFF, SUPPORT_ASSISTANT, READ_INTERNAL)],
    flagKey: FLAG_KEYS.SUPPORT_ASSISTANT_WORKSPACE,
  },
  // Không nằm trong `children` của menu cha: màn này mở từ drawer tại màn nghiệp vụ ("Mở toàn trang")
  // hoặc từ [Mở xử lý] ở danh sách, không phải một mục menu. Route thật chờ PO chốt (screen: Needs Info).
  {
    exact: true,
    path: "/support/channel",
    name: t("SupportChannel.Title"),
    component: SupportChannel,
    permissions: [permission(STAFF_BFF, SUPPORT_ASSISTANT, READ)],
    flagKey: FLAG_KEYS.SUPPORT_ASSISTANT_WORKSPACE,
  },
];
